import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { CompetitionDetail, TimelineEntry, TimelineKind } from '@/api/competitionDetail'
import { EmptyState } from '@/design'
import { drillDownHref } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'

import { statusLabel } from '../../competitions/columns'
import { formatInstant, wordLabel } from '../format'
import { describeDetailReason } from '../reasons'

const KIND_LABELS: Record<TimelineKind, string> = {
  status_changed: 'Status changed',
  started: 'Opened',
  campaign_started: 'Campaign starts',
  campaign_ended: 'Campaign ends',
  ended: 'Closed',
  draw: 'Draw',
}

/**
 * What happened in the period, oldest first: status changes, its opening, close and
 * draw, and the scheduled start and end of each campaign promoting it (scheduled dates,
 * whatever the campaign's status, not proof it ran). Price changes cannot be shown: the
 * Suite keeps the current price only.
 */
export function TimelineTab({
  detail,
  filters,
  locale,
}: {
  detail: CompetitionDetail
  filters: GlobalFilters
  locale: string | undefined
}) {
  const { timeline, timezone, notAvailable } = detail
  const priceChanges = notAvailable.priceChanges
  return (
    <div className="cd-stack">
      <p className="cd-note">
        {timeline.statusAtStart
          ? `At the start of this period it was ${statusLabel(timeline.statusAtStart).toLowerCase()}.`
          : 'It had not opened at the start of this period.'}
      </p>
      {timeline.entries.length === 0 ? (
        <EmptyState
          variant="no-results"
          compact
          title="Nothing happened in this period"
          description="No status change, opening, close, draw or campaign date for this competition falls in this period."
        />
      ) : (
        <ol className="cd-timeline" aria-label="Timeline">
          {timeline.entries.map((entry, index) => (
            <li key={`${entry.at}-${entry.kind}-${entry.campaignId ?? ''}-${index}`} className="cd-timeline__entry" data-kind={entry.kind}>
              <time dateTime={entry.at} className="cd-timeline__at">
                {formatInstant(entry.at, timezone, locale)}
              </time>
              <span className="cd-timeline__what">
                <strong>{KIND_LABELS[entry.kind]}</strong> {describe(entry, filters)}
              </span>
            </li>
          ))}
        </ol>
      )}
      {priceChanges ? (
        <p className="cd-note" data-unavailable={priceChanges}>
          Price changes: {describeDetailReason(priceChanges).label}. {describeDetailReason(priceChanges).why}
        </p>
      ) : null}
    </div>
  )
}

function describe(entry: TimelineEntry, filters: GlobalFilters): ReactNode {
  switch (entry.kind) {
    case 'status_changed':
      return entry.status ? `to ${statusLabel(entry.status)}` : null
    case 'campaign_started':
    case 'campaign_ended': {
      if (!entry.campaignId) return null
      const href = drillDownHref({ path: `/marketing/campaigns/${encodeURIComponent(entry.campaignId)}` }, filters)
      return (
        <>
          <Link to={href} className="ci-table__link">
            {entry.campaignName ?? entry.campaignId}
          </Link>
          {entry.campaignStatus ? <span className="cd-muted"> · {wordLabel(entry.campaignStatus)}</span> : null}
        </>
      )
    }
    default:
      return null
  }
}
